import { HELP_FORMULA_FUNCTION_ITEMS } from "@/components/helpNekoLanguage/formulaFunctionDocumentation";

import { Suggestion } from "./formulaEditor.types";

function extractFunctionName(signature: string): string {
	const openParenthesisIndex = signature.indexOf("(");

	if (openParenthesisIndex < 0) {
		return signature.trim();
	}

	return signature.slice(0, openParenthesisIndex).trim();
}

interface FunctionArgumentSuggestionDefinition {
	code: string;
	insertText?: string;
	label?: string;
}

interface FunctionArgumentDescriptor {
	placeholder?: string;
	placeholderInsertText?: string;
	suggestions: FunctionArgumentSuggestionDefinition[];
}

interface FunctionOverloadDescriptor {
	arguments: FunctionArgumentDescriptor[];
	signature: string;
}
const functionItems = HELP_FORMULA_FUNCTION_ITEMS;
const functionSignaturesByName = new Map<string, string[]>();

for (const item of functionItems) {
	const signature = item.code.trim();
	const functionName = extractFunctionName(signature);
	const signatures = functionSignaturesByName.get(functionName) || [];
	signatures.push(signature);
	functionSignaturesByName.set(functionName, signatures);
}

export const FORMULA_FUNCTION_SUGGESTIONS: Suggestion[] = Array.from(functionSignaturesByName.entries()).map(
	([functionName, signatures]) => ({
		code: functionName,
		insertText: `${functionName}(`,
		kind: "function",
		label: signatures.join(" | "),
		section: "Fonctions"
	})
);

function splitSignatureArguments(argsText: string): string[] {
	const normalizedArgsText = argsText.replace(/[\[\]]/g, "");
	const args: string[] = [];
	let current = "";
	let inString = false;
	let angleDepth = 0;
	let parenDepth = 0;

	for (let index = 0; index < normalizedArgsText.length; index++) {
		const character = normalizedArgsText[index];

		if (character === '"') {
			inString = !inString;
			current += character;
			continue;
		}

		if (!inString) {
			if (character === "<") {
				angleDepth++;
			} else if (character === ">") {
				angleDepth = Math.max(0, angleDepth - 1);
			} else if (character === "(") {
				parenDepth++;
			} else if (character === ")") {
				parenDepth = Math.max(0, parenDepth - 1);
			} else if (character === "," && !angleDepth && !parenDepth) {
				args.push(current.trim());
				current = "";
				continue;
			}
		}

		current += character;
	}

	if (current.trim()) {
		args.push(current.trim());
	}

	return args;
}

function stripWrappingDelimiters(value: string): string {
	let strippedValue = value.trim();
	let changed = true;

	while (changed) {
		changed = false;

		if (strippedValue.startsWith("[") && strippedValue.endsWith("]")) {
			strippedValue = strippedValue.slice(1, -1).trim();
			changed = true;
		}

		if (strippedValue.startsWith("(") && strippedValue.endsWith(")")) {
			strippedValue = strippedValue.slice(1, -1).trim();
			changed = true;
		}
	}

	return strippedValue;
}

function extractLiteralSuggestions(argumentSignature: string): FunctionArgumentSuggestionDefinition[] {
	const stringLiterals = Array.from(argumentSignature.matchAll(/"([^"]+)"/g)).map(match => match[1]);
	if (stringLiterals.length) {
		return stringLiterals.map(value => ({
			code: value,
			insertText: `"${value}"`
		}));
	}

	if (!argumentSignature.includes("|")) {
		return [];
	}

	const numericLiterals = Array.from(argumentSignature.matchAll(/(?:^|[|(])\s*(-?\d+(?:[.,]\d+)?)\s*(?=$|[|)])/g)).map(
		match => match[1]
	);

	return numericLiterals.map(value => ({ code: value }));
}

function extractPlaceholder(argumentSignature: string): string | undefined {
	const match = argumentSignature.match(/<([^>]+)>/);
	return match?.[1]?.trim();
}

function inferPlaceholderInsertText(placeholder?: string): string | undefined {
	return placeholder ? `<${placeholder}>` : undefined;
}

function createFunctionArgumentDescriptor(
	functionName: string,
	argumentSignature: string,
	argumentIndex: number
): FunctionArgumentDescriptor {
	const cleanedArgument = stripWrappingDelimiters(argumentSignature);
	const placeholder = extractPlaceholder(cleanedArgument);

	return {
		placeholder,
		placeholderInsertText: inferPlaceholderInsertText(placeholder),
		suggestions: extractLiteralSuggestions(cleanedArgument)
	};
}

function buildFunctionOverloads(signatures: string[], functionName: string): FunctionOverloadDescriptor[] {
	return signatures
		.map(signature => {
			const openParenthesisIndex = signature.indexOf("(");
			const closeParenthesisIndex = signature.lastIndexOf(")");
			if (openParenthesisIndex < 0 || closeParenthesisIndex <= openParenthesisIndex) {
				return null;
			}

			return {
				arguments: splitSignatureArguments(signature.slice(openParenthesisIndex + 1, closeParenthesisIndex)).map(
					(argumentSignature, index) => createFunctionArgumentDescriptor(functionName, argumentSignature, index)
				),
				signature
			};
		})
		.filter((descriptor): descriptor is FunctionOverloadDescriptor => descriptor !== null);
}

const FUNCTION_OVERLOADS = new Map<string, FunctionOverloadDescriptor[]>(
	Array.from(functionSignaturesByName.entries()).map(([functionName, signatures]) => [
		functionName.toLowerCase(),
		buildFunctionOverloads(signatures, functionName)
	])
);

function buildLiteralSuggestions(
	candidateOverloads: FunctionOverloadDescriptor[],
	currentArgIndex: number,
	currentArgValue: string
): Suggestion[] {
	const normalizedValue = currentArgValue.toLowerCase();
	const suggestionsByKey = new Map<string, { code: string; insertText: string; signatures: string[] }>();

	for (const overload of candidateOverloads) {
		const argumentDescriptor = overload.arguments[currentArgIndex];
		if (!argumentDescriptor) {
			continue;
		}

		for (const suggestion of argumentDescriptor.suggestions) {
			if (!suggestion.code.toLowerCase().includes(normalizedValue)) {
				continue;
			}

			const insertText = suggestion.insertText || suggestion.code;
			const suggestionKey = `${suggestion.code}::${insertText}`;
			const existingSuggestion = suggestionsByKey.get(suggestionKey);

			if (existingSuggestion) {
				if (!existingSuggestion.signatures.includes(overload.signature)) {
					existingSuggestion.signatures.push(overload.signature);
				}
				continue;
			}

			suggestionsByKey.set(suggestionKey, {
				code: suggestion.code,
				insertText,
				signatures: [overload.signature]
			});
		}
	}

	return Array.from(suggestionsByKey.values()).map(suggestion => ({
		code: suggestion.code,
		insertText: suggestion.insertText,
		kind: "value" as const,
		label: suggestion.signatures.join(" | "),
		section: "Valeurs de fonction"
	}));
}

function buildPlaceholderSuggestions(
	candidateOverloads: FunctionOverloadDescriptor[],
	currentArgIndex: number
): Suggestion[] {
	const suggestionsByKey = new Map<string, Suggestion>();

	for (const overload of candidateOverloads) {
		const argumentDescriptor = overload.arguments[currentArgIndex];
		if (!argumentDescriptor?.placeholder) {
			continue;
		}

		const insertText = argumentDescriptor.placeholderInsertText || `"${argumentDescriptor.placeholder}"`;
		const displayCode = argumentDescriptor.placeholder;
		const suggestionKey = `${displayCode}::${insertText}::${overload.signature}`;
		if (suggestionsByKey.has(suggestionKey)) {
			continue;
		}

		suggestionsByKey.set(suggestionKey, {
			code: displayCode,
			insertText,
			kind: "value",
			label: overload.signature,
			section: "Argument attendu"
		});
	}

	return Array.from(suggestionsByKey.values());
}

export function getFunctionSuggestions(code: string): Suggestion[] {
	const normalizedCode = code.toLowerCase();

	return FORMULA_FUNCTION_SUGGESTIONS.filter(suggestion => {
		return suggestion.code.toLowerCase().includes(normalizedCode);
	});
}

export function getFunctionArgumentSuggestions(
	functionName: string,
	currentArgIndex: number,
	currentArgValue: string
): Suggestion[] {
	const candidateOverloads = (FUNCTION_OVERLOADS.get(functionName.toLowerCase()) || []).filter(overload => {
		return overload.arguments[currentArgIndex] != null;
	});

	if (!candidateOverloads.length) {
		return [];
	}

	const contextualSuggestions = buildLiteralSuggestions(candidateOverloads, currentArgIndex, currentArgValue);

	if (contextualSuggestions.length || currentArgValue.trim()) {
		return contextualSuggestions;
	}

	return buildPlaceholderSuggestions(candidateOverloads, currentArgIndex);
}
